import React, { lazy, useState, useEffect, useMemo, useContext } from "react";
import {
  FormGroup,
  onMount,
  NumberField,
  withPopover,
  PendingButton,
  AutoTextArea
} from "components/common";
import { Control, track, Fieldset, actions } from "react-redux-form";
import { useLocation } from 'react-router-dom';
import Select from "react-select";
import SelectPlus from "react-select-plus";
import EmployeeContext from "contexts/EmployeeContext";

import {
  Row,
  Col,
  Button,
  UncontrolledButtonDropdown,
  DropdownItem,
  DropdownToggle,
  DropdownMenu,
  PopoverHeader,
  PopoverBody,
  ModalBody,
  ModalHeader, Table,
  Input
} from "reactstrap";
import { Trans, useTranslation, withTranslation } from "react-i18next";
import moment from "moment";
import { Link } from "react-router-dom";
import {
  compose,
  withStateHandlers,
  withPropsOnChange,
  withHandlers,
} from "recompose";
import classNames from "classnames";
import {
  keyBy,
  chain,
  capitalize,
  isUndefined,
  transform,
  forEach,
  keys,
  find,
  sumBy,
  includes,
  compact,
  get,
  range,
  set,
  findIndex, isEmpty,
} from "lodash";
import { formatNumber, formatCurrency, CurrencySymbol } from "services/util";
import diff from "deep-diff";
import shortid from "shortid";
import { withOrganisationContextConsumer } from "contexts/OrganisationContext";
import useModal from "../hooks/useModal";
import { useConfirmModal, useXeroClient } from "../hooks";
import { AddNewItemSelect } from "./AddNewItemSelect";
import { NotificationManager } from "react-notifications";
import { useQuery } from "react-query";

const EditInventory = lazy(() =>
  import("../pages/Xero/Inventory/EditInventory")
);

const numberStyle = {
  width: 80,
};


export const getDocumentType = (type) => {
  const isReceivableInvoice = type === "ACCREC";
  const isPayableInvoice = type === "ACCPAY";
  const isReceivableCreditNote = type === "ACCRECCREDIT";
  const isPayableCreditNote = type === "ACCPAYCREDIT";
  const isSpend = type === "SPEND";
  const isReceive = type === "RECEIVE";
  const isSpendTransfer = type === "SPEND-TRANSFER";
  const isReceiveTransfer = type === "RECEIVE-TRANSFER";
  const isPurchaseOrder = type === "PURCHASEORDER";
  const isSpendPrepayment = type === "SPEND-PREPAYMENT";
  const isSpendOverepayment = type === "SPEND-OVERPAYMENT";
  const isReceivePrepayment = type === "RECEIVE-PREPAYMENT";
  const isReceiveOverepayment = type === "RECEIVE-OVERPAYMENT";


  return {
    isReceivableInvoice,
    isReceivableCreditNote,
    isPayableInvoice,
    isPayableCreditNote,
    isSpend,
    isSpendTransfer,
    isReceive,
    isReceiveTransfer,
    isPurchaseOrder,
    isSpendOverepayment,
    isSpendPrepayment,
    isReceiveOverepayment,
    isReceivePrepayment,
  };
};

export const isEmptyLineItem = (v) => {
  const line = chain(v)
    .pick([
      "Description",
      "Quantity",
      "UnitAmount",
      "ItemCode",
      "AccountCode",
    ])
    .pickBy()
    .value();

  return isEmpty(line);
};

export const getBlankLineItems = (num = 5) =>
  chain(num)
    .range()
    .map((i) => ({
      _id: shortid(),
    }))
    .value();

export const filterAndValidateLineItems = (dispatch) => {
  return (lineItems) => {
    return lineItems.reduce(
      (acc, i, index) => {
        const { result } = acc;

        if (!isEmptyLineItem(i)) {
          result.push(i);
        }

        return acc;
      },
      {
        error: false,
        result: [],
      }
    );
  };
};

const recalculate = (
  prevItem,
  {
    taxRates,
    dispatch,
    organisation,
    includeDiscount,
    isReceivable,
    isPayable,
    indexedInventory,
  }
) =>
  dispatch &&
  dispatch((_, getState) => {
    let local = getState().local;

    if (!local.LineAmountTypes) {
      dispatch(actions.change("local.LineAmountTypes", "Exclusive"));
    }
    if (!local.CurrencyCode) {
      dispatch(actions.change("local.CurrencyCode", organisation.BaseCurrency));
    }
    if (!local.LineItems.length && local.Contact) {
      const customerDiscount = get(local, "Contact.Discount") || undefined;
      const customerTaxType =
        get(
          local,
          isReceivable
            ? "Contact.AccountsReceivableTaxType"
            : "Contact.AccountsPayableTaxType"
        ) || undefined;
      const customerAccountCode =
        get(
          local,
          isReceivable
            ? "Contact.SalesDefaultAccountCode"
            : isPayable
              ? "Contact.PurchasesDefaultAccountCode"
              : null
        ) || undefined;

      dispatch(
        actions.push("local.LineItems", {
          _id: shortid(),
          DiscountRate: includeDiscount ? customerDiscount : undefined,
          TaxType: customerTaxType,
          AccountCode: customerAccountCode,
        })
      );
    }
    local = getState().local;

    let changed = null;
    let useTaxRate = null;

    if (prevItem) {
      const itemDiff = diff(prevItem, local);
      if (!itemDiff) return;

      useTaxRate = itemDiff.find(item => (item.path.includes("LineAmountTypes") || item.path.includes("TaxType") || item.path.includes("Quantity") || item.path.includes("DiscountRate") || item.path.includes("UnitAmount")) && (item.kind === "E" || (item.kind==="N" && item.path.includes("DiscountRate"))));

      changed = transform(
        itemDiff,
        (result, { path, kind }) => {
          if (
            path[0] === "LineAmountTypes" ||
            (path[0] === "LineItems" && kind === "A")
          ) {
            result.recalculateAllLines = true;
          } else if (path[0] === "LineItems" && kind !== "A") {
            switch (path[2]) {
              case "AccountCode":
                break;
              case "DiscountRate":
              case "UnitAmount":
              case "Quantity":
              case "TaxAmount":
              case "TaxType":
                result.linesChanged.push(path[1]);
                break;
              default:
                break;
            }
          }
        },
        {
          linesChanged: [],
        }
      );
    } else {
      changed = {
        recalculateAllLines: true,
      };
    }

    if (changed.recalculateAllLines) {
      changed.linesChanged = keys(local.LineItems);
    }

    forEach(changed.linesChanged, (idx) => {
      const line = local.LineItems[idx];
      const discountRate = includeDiscount
        ? parseFloat(line?.DiscountRate) / 100 || 0
        : 0;
      const unitAmount = parseFloat(line.UnitAmount) || 0;
      const quantity = parseFloat(line.Quantity) || 0;
      const tax_amount = parseFloat(line.TaxAmount) || 0;
      let lineAmount = unitAmount * quantity;
      const discountAmount = lineAmount * discountRate;
      lineAmount -= discountAmount;

      const taxRate = find(taxRates, { TaxType: line.TaxType });
      let taxAmount = 0;

      if (local.LineAmountTypes === "NoTax") {
        dispatch(actions.omit(`local.LineItems[${idx}]`, ["TaxType"]));
      } else if (local.LineAmountTypes === "Exclusive") {
        if (tax_amount && !useTaxRate) {
          taxAmount = tax_amount;
        } else if (taxRate) {
          taxAmount = lineAmount * (taxRate.EffectiveRate / 100);
        }

      } else if (local.LineAmountTypes === "Inclusive") {
        if (tax_amount && !useTaxRate) {
          taxAmount = tax_amount;
        } else if (taxRate) {
          taxAmount = lineAmount - (lineAmount / (1 + taxRate.EffectiveRate / 100));
        }
      }

      taxAmount = (Math.round((taxAmount * 100).toFixed(2)) / 100).toFixed(2);

      const item = indexedInventory[line.ItemCode] ?? {};
      const { IsTrackedAsInventory, InventoryAssetAccountCode } = item;
      const Type = get(local, "Type") || undefined;

      if (IsTrackedAsInventory && InventoryAssetAccountCode && isPayable) {
        dispatch(
          actions.change(
            `local.LineItems[${idx}].AccountCode`,
            InventoryAssetAccountCode
          )
        );
        dispatch(
          actions.change(`local.LineItems[${idx}].AccountCodeLocked`, true)
        );
      } else if (Type === "SPEND-OVERPAYMENT" || Type === "RECEIVE-OVERPAYMENT") {
        dispatch(
          actions.change(`local.LineItems[${idx}].AccountCodeLocked`, true)
        );
      } else {
        dispatch(
          actions.change(`local.LineItems[${idx}].AccountCodeLocked`, false)
        );
      }

      dispatch(
        actions.change(
          `local.LineItems[${idx}].TaxAmount`,
          formatNumber(taxAmount)
        )
      );
      dispatch(
        actions.change(
          `local.LineItems[${idx}].BaseTaxAmount`,
          formatNumber(taxAmount)
        )
      );
      dispatch(
        actions.change(
          `local.LineItems[${idx}].LineAmount`,
          formatNumber(lineAmount)
        )
      );

      dispatch(
        actions.change(
          `local.LineItems[${idx}].DiscountAmount`,
          includeDiscount && discountAmount ? formatNumber(discountAmount) : undefined
        )
      );
    });

    if (changed.linesChanged.length || changed.recalculateAllLines) {
      local = getState().local;
      const amountPaid = parseFloat(local.AmountPaid) || 0;
      const creditAmount = parseFloat(local.AmountCredited) || 0;

      const subTotal = sumBy(
        local.LineItems,
        (v) => parseFloat(v.LineAmount) || 0
      );
      const totalTax = sumBy(
        local.LineItems,
        (v) => parseFloat(v.TaxAmount) || 0
      );
      const baseTax = sumBy(
        local.LineItems,
        (v) => parseFloat(v.BaseTaxAmount) || 0
      );

      const totalDiscount = sumBy(
        local.LineItems,
        (v) => parseFloat(v.DiscountAmount) || 0
      );

      dispatch(actions.change("local.SubTotal", formatNumber(subTotal)));
      dispatch(actions.change("local.TotalTax", formatNumber(totalTax)));
      dispatch(
        actions.change("local.TotalDiscount", formatNumber(totalDiscount))
      );

      if (
        local.LineAmountTypes === "NoTax" ||
        local.LineAmountTypes === "Inclusive"
      ) {
        dispatch(actions.change("local.Total", formatNumber(subTotal)));

        dispatch(
          actions.change(
            "local.AmountDue",
            formatNumber(subTotal - amountPaid - creditAmount)
          )
        );
      } else if (local.LineAmountTypes === "Exclusive") {
        dispatch(
          actions.change("local.Total", formatNumber(subTotal + totalTax))
        );
        dispatch(
          actions.change("local.Adjustments", formatNumber(baseTax - totalTax))
        );

        dispatch(
          actions.change(
            "local.AmountDue",
            formatNumber(subTotal + totalTax - amountPaid - creditAmount)
          )
        );
      }
    }
  });

const DeleteAllocationButton = ({ url, onDeleted }) => {
  const { Modal, setIsOpen } = useConfirmModal();
  const client = useXeroClient();
  const { t } = useTranslation();

  async function onSubmit() {
    await client.delete(url);
    NotificationManager.success(t("Allocation removed"));
    setIsOpen(false);
    await onDeleted?.();
  }

  return (
    <>
      <Modal onConfirm={onSubmit}>
        <ModalHeader>
          <Trans>Confirm</Trans>
        </ModalHeader>
        <ModalBody>
          <Trans>Are you sure you want to delete this credit allocation?</Trans>
        </ModalBody>
      </Modal>
      <Button
        size="sm"
        className="text-danger ml-1"
        onClick={() => setIsOpen(true)}
        color="link"
      >
        <span className="fa fa-trash" />
      </Button>
    </>
  );
};

const TrackingSelect = ({
  lineItem,
  trackingCategory,
  lineItemIndex,
  dispatch,
}) => {
  const { Tracking } = lineItem;

  const selectedTracking = useMemo(() => {
    return (
      Tracking?.reduce((acc, i) => {
        acc[i.Name] = i.Option;
        return acc;
      }, {}) ?? {}
    );
  }, [Tracking]);

  return (
    <div className="select-container" style={{top: `${76+59*lineItemIndex}px` }}>
      <Select
        labelKey="Name"
        value={selectedTracking[trackingCategory.Name] ?? null}
        onChange={(v) => {
          const { Name } = trackingCategory;
          const index = findIndex(Tracking, { Name });

          const payload = {
            Name,
            Option: v?.Name,
          };

          const model = `local.LineItems.${lineItemIndex}.Tracking`;

          if (v) {
            if (index === -1) {
              dispatch(actions.push(model, payload));
            } else {
              dispatch(actions.change(`${model}.${index}`, payload));
            }
          } else {
            if (index >= 0) {
              dispatch(actions.remove(model, index));
            }
          }
        }}
        valueKey="Name"
        style={{ width: 150 }}
        options={trackingCategory.Options}
      />
    </div>
  );
};

// TODO: check delete functionality keeps a row in place, not a big deal
export const LineItems = ({
  item,
  currencies,
  lineAmountTypes,
  dispatch,
  accounts,
  taxRates,
  removeLine,
  handleAddLines,
  hideAddLines,
  hideAccountAndTax,
  organisation,
  paymentText = "Less Payment",
  prepaymentText = "Less Prepayment",
  inventory: inventoryProp,
  Popover,
  includeDiscount,
  includeItems = true,
  togglePopover,
  isPayable,
  isReceivable,
  quantityReadOnly,
  onAllocationDeleted,
  isNewInvoice,
  fiveNewLines,
  onDelete,
  pending,
  accountReadOnly
}) => {

  const filteredAccounts = accounts.map((group) => ({
    ...group,
    options: group.options.filter((account) => account.Type !== "BANK"),
  }));
  const currencies_ = useMemo(() => currencies.map((c) => ({ label: <CurrencySymbol key={c.Code} currency={c.Code}/>, value: c.Code})), [currencies])
  const { Modal, setIsOpen, toggle, isOpen } = useModal();
  const [newItemIndex, setNewItemIndex] = useState();
  const [showTaxAmount, setShowTaxAmount] = useState(false);
  const [inventory, setInventory] = useState(inventoryProp);
  //const { Type } = item;
  const xero = useXeroClient();
  const { t } = useTranslation();
  //const type = useMemo(() => getDocumentType(Type), [Type]);
  const { employee } = useContext(EmployeeContext);
  const { data: trackingCategories } = useQuery(
    [
      "trackingcategories",
      {
        params: {
          Status: "ACTIVE",
        },
      },
    ],
    ({ queryKey }) => xero.get(...queryKey).get("data"),
    {
      placeholderData: [],
    }
  );

  useEffect(() => {
    setInventory(inventoryProp);
  }, [inventoryProp]);

  function onNewItemSaved(x) {
    x = {
      ...x,
      Description: compact([x.Code, x.Name]).join(": "),
    };

    setInventory((v) => [...v, x]);
    itemCodeChangeAction(newItemIndex)(
      `local.LineItems.${newItemIndex}.ItemCode`,
      x
    );
    setIsOpen(false);
  }

  const { Total } = item;

  useEffect(() => {
    dispatch(
      actions.map("local.Allocations", (i) => {
        if (!i.IsNew) return i;

        let amount = Total;
        const amountDue = i.Invoice?.AmountDue ?? null;

        if (amountDue !== null) {
          amount = Math.min(Number(amountDue), Number(Total));
          amount = isNaN(amount) ? "" : amount.toFixed(2);
        }

        return {
          ...i,
          Amount: amount,
        };
      })
    );
    dispatch(
      actions.map("local.LineItems", (i) => {
        const line = { ...i };
        if (!includeDiscount) {
          delete line.DiscountRate;
          delete line.DiscountAmount;
        }
        return line;
      })
    );
  }, [Total]);



  const itemCodeChangeAction = (idx) => (m, v) => {
    dispatch(actions.change(m, v ? v.Code : undefined));
    const lineItem = item.LineItems[idx];

    if (!v) return;
    let updates = {
      Quantity: "1.00",
    };

    if (isReceivable) {
      updates = {
        ...updates,
        Description: v.Description || v.Name,
        AccountCode:
          lineItem.AccountCode || get(v, "SalesDetails.AccountCode") || null,
        TaxType: lineItem.TaxType || get(v, "SalesDetails.TaxType") || null,
        UnitAmount: get(v, "SalesDetails.UnitPrice") || "1.00",
      };
    } else if (isPayable) {
      updates = {
        ...updates,
        Description: v.PurchaseDescription || v.Name,
        AccountCode:
          v.InventoryAssetAccountCode ||
          lineItem.AccountCode ||
          get(v, "PurchaseDetails.AccountCode") ||
          null,
        TaxType: lineItem.TaxType || get(v, "PurchaseDetails.TaxType") || null,
        UnitAmount: get(v, "PurchaseDetails.UnitPrice") || "1.00",
      };
    }

    forEach(updates, (v, m) =>
      dispatch(actions.change(`local.LineItems[${idx}.${m}`, v))
    );
  };

  const location = useLocation();

  useEffect(() => {
    if (location.pathname.includes("new") && fiveNewLines === true) {
      handleAddLines(5);
    }
  }, [fiveNewLines]);

  return (
    <div>
      {isOpen ? (
        <Modal size="lg">
          <ModalHeader toggle={toggle}>
            <Trans>New Item</Trans>
          </ModalHeader>
          <ModalBody>
            <EditInventory
              match={{ params: { id: "new" } }}
              t={t}
              xero={xero}
              inline
              onSaved={onNewItemSaved}
            />
          </ModalBody>
        </Modal>
      ) : null}
      <Row className="mb-1">
        <Col>
          <FormGroup
            label="Currency"
            model=".CurrencyCode"
            addControlClass={false}
          >
            <Control.select
              component={Select}
              model=".CurrencyCode"
              clearable={false}
              disabled={!isNewInvoice}
              readOnly={isNewInvoice}
              options={currencies_}
              simpleValue
              style={{ backgroundColor: '#F4F7FE' }}
            />
            {organisation &&
              organisation.BaseCurrency !== item.CurrencyCode &&
              item.CurrencyRate ? (
              <div className="d-flex align-items-center form-text text-muted">
                <span>
                  1 <CurrencySymbol currency={organisation.BaseCurrency}/> = {item.CurrencyRate}{" "}
                  <CurrencySymbol currency={item.CurrencyCode}/>
                </span>
                <Button
                  size="sm"
                  onClick={togglePopover}
                  color="link"
                  id="set-exchange"
                >
                  <Trans>Set exchange rate</Trans>
                </Button>
                <Popover target="#set-exchange">
                  <PopoverHeader>
                    <Trans>Set exchange rate</Trans>
                  </PopoverHeader>
                  <PopoverBody>
                    <FormGroup label="">
                      <div className="d-flex align-items-center">
                        1 <CurrencySymbol currency={organisation.BaseCurrency}/>  ={" "}
                        <NumberField
                          step="any"
                          model=".CurrencyRate"
                          updateOn="blur"
                          className="form-control mx-1"
                          style={{ maxWidth: "6rem" }}
                        />{" "}
                        {item.CurrencyCode}
                      </div>
                    </FormGroup>
                  </PopoverBody>
                </Popover>
              </div>
            ) : null}
          </FormGroup>
        </Col>
        <Col>
          <FormGroup
            label="Amounts are"
            model=".LineAmountTypes"
            addControlClass={false}
          >
            <Control.select style={{ backgroundColor: '#F4F7FE' }}
              component={Select}
              model=".LineAmountTypes"
              options={lineAmountTypes}
              disabled={!!hideAccountAndTax}
              simpleValue
              clearable={false}
              searchable={false}
            />
          </FormGroup>
        </Col>
      </Row>
      <div className="line-items">
        {/* AE update table to Table reactStrap */}
        <Table className="table table-sm small line-items--table" style={{marginBottom:"120px"}} responsive >
          <thead>
            <tr>
              <th
                className={classNames("text-nowrap with-select", {
                  "d-none": !includeItems,
                })}
              >
                <Trans>Item</Trans>
              </th>
              <th className="text-nowrap w-100">
                <Trans>Description</Trans>
              </th>
              <th className="text-nowrap">
                <Trans>Qty</Trans>
              </th>
              <th className="text-nowrap">
                <Trans>Unit Price</Trans>
              </th>
              {includeDiscount && (
                <th className="text-nowrap">
                  <Trans>Disc %</Trans>
                </th>
              )}
              {!hideAccountAndTax ? (
                <>
                  <th className="text-nowrap w-100 with-select">
                    <Trans>Account</Trans>
                  </th>
                  <th
                    className={`text-nowrap with-select ${item.LineAmountTypes === "NoTax" ? "d-none" : ""
                      }`}
                  >
                    <Trans>Tax Rate</Trans>

                    <Button
                      size="sm"
                      color="link"
                      onClick={() => setShowTaxAmount((v) => !v)}
                      className="ml-1 py-0"
                    >
                      <span className={`fa fa-plus ${showTaxAmount? "fa-minus":"fa-plus"}`} style={{ color: '#72B566' }} />
                    </Button>
                  </th>
                  <th
                    className={`text-nowrap ${item.LineAmountTypes !== "Exclusive" || !showTaxAmount
                        ? "d-none"
                        : ""
                      }`}
                  >
                    <Trans>Tax Amount</Trans>
                  </th>
                </>
              ) : null}
              {trackingCategories.map((i) => (
                <th key={i.TrackingCategoryID} className="with-select">{i.Name}</th>
              ))}
              <th className="text-nowrap">
                <Trans>Amount</Trans>
                <CurrencySymbol currency={item.CurrencyCode} />
              </th>
              <th />
              <th />
            </tr>
          </thead>
          <tbody className="selects-container">
            {item.LineItems.map((i, idx) => {
              const tracker = track(".LineItems[]", { _id: i._id });
              return (
                <Fieldset component="tr" model={tracker} key={i._id}>
                  <td className={!includeItems ? "d-none" : ""}>
                    <div className="select-container" >
                      <Control
                        component={AddNewItemSelect}
                        model=".ItemCode"
                        onNewItem={() => {
                          setNewItemIndex(idx);
                          setIsOpen(true);
                        }}
                        labelKey="Key"
                        valueKey="Code"
                        options={inventory}
                        changeAction={itemCodeChangeAction(idx)}
                      />
                    </div>
                  </td>
                  <td>
                    <FormGroup model=".Description" className="mb-0">
                      <AutoTextArea
                        model=".Description"
                        updateOn="blur"
                        type="textarea"
                        rows={1}
                        id={`textarea-${idx}`}
                      />
                    </FormGroup>
                  </td>
                  <td>
                    <NumberField
                      model=".Quantity"
                      updateOn="blur"
                      readOnly={quantityReadOnly}
                      style={numberStyle}
                      className="form-control no-spinner"
                    />
                  </td>
                  <td>
                    <NumberField
                      model=".UnitAmount"
                      updateOn="blur"
                      style={numberStyle}
                      className="form-control no-spinner"
                    />
                  </td>
                  {includeDiscount && (
                    <td>
                      <NumberField
                        model=".DiscountRate"
                        updateOn="blur"
                        style={numberStyle}
                        className="form-control no-spinner"
                      />
                    </td>
                  )}
                  {!hideAccountAndTax ? (
                    <>
                      <td>
                        <div className="select-container" >
                          <Control
                            component={SelectPlus}
                            model=".AccountCode"
                            labelKey="Description"
                            valueKey="Code"
                            style={{ width: 150 }}
                            options={filteredAccounts}
                            disabled={!!(i.AccountCodeLocked || accountReadOnly)}
                            changeAction={(m, v) => {
                              dispatch(actions.change(m, v ? v.Code : null));
                              dispatch(
                                actions.change(
                                  `local.LineItems.${idx}.TaxType`,
                                  v ? v.TaxType : null
                                )
                              );
                            }}
                          />
                        </div>
                      </td>
                      <td
                        className={
                          item.LineAmountTypes === "NoTax" ? "d-none" : ""
                        }
                      >
                        <div className="select-container" >
                          <Control
                            component={Select}
                            model=".TaxType"
                            labelKey="Name"
                            valueKey="TaxType"
                            style={{ width: 150 }}
                            options={taxRates}
                            simpleValue
                          />
                        </div>
                      </td>
                      <td
                        className={
                          item.LineAmountTypes !== "Exclusive" || !showTaxAmount
                            ? "d-none"
                            : ""
                        }
                      >
                        <NumberField
                          model=".TaxAmount"
                          updateOn="blur"
                          style={numberStyle}
                          className="form-control"
                          type="number"
                        />
                      </td>
                    </>
                  ) : null}
                  {trackingCategories.map((j) => (
                    <td key={j.TrackingCategoryID}>
                      <TrackingSelect
                        lineItem={i}
                        trackingCategory={j}
                        dispatch={dispatch}
                        lineItemIndex={idx}
                      />
                    </td>
                  ))}
                  <td>
                    <Control.text
                      model=".LineAmount"
                      style={numberStyle}
                      readOnly
                      className="form-control"
                    />
                  </td>
                  <td className="">
                    <Button color="link" size="sm" onClick={() => removeLine(idx)}>
                      <span className="fa fa-times text-danger" />
                    </Button>
                  </td>
                </Fieldset>
              );
            })}
          </tbody>
        </Table>
      </div>
      <div className="d-flex align-items-start line-items-footer-responsive mt-2">
        {!hideAddLines ? (
          <UncontrolledButtonDropdown >
            <Button color="success" onClick={() => handleAddLines(1)}>
              <Trans>Add a new line</Trans>
            </Button>
            <DropdownToggle caret color="success" />
            <DropdownMenu>
              <DropdownItem onClick={() => handleAddLines(5)}>
                <Trans>Add 5</Trans>
              </DropdownItem>
              <DropdownItem onClick={() => handleAddLines(10)}>
                <Trans>Add 10</Trans>
              </DropdownItem>
              <DropdownItem onClick={() => handleAddLines(20)}>
                <Trans>Add 20</Trans>
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        ) : null}
        <div className="mx-auto" />
        <table className="mb-3">
          <tbody className="text-right">
            <tr>
              <td>
                <Trans>Subtotal</Trans>
                {Number(item.TotalDiscount) > 0 ? (
                  <>
                    {" "}
                    <span className="text-muted tw-text-sm">
                      <Trans>
                        (includes a discount of{" "}
                        {{ amount: formatCurrency(item.TotalDiscount) }})
                      </Trans>
                    </span>
                  </>
                ) : null}
              </td>
              <td>{formatCurrency(item.SubTotal)}</td>
            </tr>
            {item.LineAmountTypes === "Inclusive" && (
              <tr>
                <td>
                  <Trans>Includes Tax</Trans>
                </td>
                <td>{formatCurrency(item.TotalTax)}</td>
              </tr>
            )}
            {item.LineAmountTypes === "Exclusive" && (
                <tr>
                  <td>
                    <Trans>Total Tax</Trans>
                  </td>
                  <td>{formatCurrency(item.TotalTax)}</td>
                </tr>
            )}
            <tr className="border-top border-bottom">
              <td className="h4 py-1">
                <Trans>Total</Trans>
              </td>
              <td className="h4" style={{ minWidth: "8rem" }}>
                {formatCurrency(item.Total)}
              </td>
            </tr>
            {!employee && !!item.Payments &&
              item.Payments.map((i) => (
                <tr key={i.PaymentID} className="border-top border-bottom">
                  <td>
                    <Link to={`/payments/${i.PaymentID}`}>
                      <Trans>{paymentText}</Trans>
                    </Link>
                    <br />
                    {moment(i.Date).format("YYYY-MM-DD")}
                  </td>
                  <td>{formatCurrency(i.Amount)}</td>
                </tr>
              ))}
            {!employee && !!item.Prepayments &&
              item.Prepayments.map((i) => (
                <tr key={i.PrepaymentID} className="border-top border-bottom">
                  <td>
                    <Trans>{prepaymentText}</Trans>
                    <br />
                    {moment(i.Date).format("YYYY-MM-DD")}
                  </td>
                  <td>{formatCurrency(i.AppliedAmount)}</td>
                </tr>
              ))}
            {!employee && !!item.CreditNotes &&
              item.CreditNotes.map((i, idx) => (
                <tr key={idx} className="border-top border-bottom">
                  <td>
                    <Trans>
                      Less{" "}
                      <Link
                        to={`/${isPayable?"purchases":"accounts_receivable"}/credit_notes/edit/${i.CreditNoteID}`}
                      >
                        Credit Note {{ CreditNoteNumber: i.CreditNoteNumber }}
                      </Link>
                    </Trans>
                    <br />
                    {moment(i.Date).format("YYYY-MM-DD")}
                  </td>
                  <td>{formatCurrency(i.AppliedAmount)}</td>
                </tr>
              ))}
            {!employee && !!item.Overpayments &&
              item.Overpayments.map((i) => (
                <tr key={i.OverpaymentID} className="border-top border-bottom">
                  <td>
                    <Trans>
                      Less{" "}
                      <Link
                        to={`/overpayments/${i.OverpaymentID}`}
                      >
                        Overpayment
                      </Link>
                    </Trans>
                    <br />
                    {moment(i.Date).format("YYYY-MM-DD")}
                  </td>
                  <td>{formatCurrency(i.AppliedAmount)}</td>
                </tr>
              ))}
            {!employee && !!item.Allocations &&
              item.Allocations.map((i, idx) => (
                <tr key={idx} className="border-top border-bottom">
                  <td>
                    <Trans>
                      Less Credit
                      {!!i.Invoice && (
                        <span>
                          {" "}
                          to{" "}
                          <Link
                            to={`/${isPayable?"purchases":"accounts_receivable"}/invoices/edit/${i.Invoice.InvoiceID}`}
                          >
                            {{ InvoiceNumber: i.Invoice.InvoiceNumber }}
                          </Link>
                        </span>
                      )}
                    </Trans>
                    <br />
                    {moment(i.Date).format("YYYY-MM-DD")}
                  </td>
                  <td>
                    {formatCurrency(i.Amount)}
                    {item.CreditNoteID && i.AllocationID ? (
                      <DeleteAllocationButton
                        url={`creditnotes/${item.CreditNoteID}/allocations/${i.AllocationID}`}
                        onDeleted={onAllocationDeleted}
                      />
                    ) : null}
                  </td>
                </tr>
              ))}
            {!isUndefined(item.AmountDue) && (
              <tr className="border-top border-bottom">
                <td className="h4 py-1">
                  <Trans>Amount Due</Trans>
                </td>
                <td className="h4">{formatCurrency(item.AmountDue)}</td>
              </tr>
            )}
            {!isUndefined(item.RemainingCredit) && (
              <tr className="border-top border-bottom">
                <td className="h4 py-1">
                  <Trans>Remaining Credit</Trans>
                </td>
                <td className="h4">{formatCurrency(item.RemainingCredit)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const withLineItems = compose(
  withOrganisationContextConsumer,
  withTranslation(),
  withStateHandlers(
    ({ t }) => ({
      lineAmountTypes: [
        {
          label: t("Tax Inclusive"),
          value: "Inclusive",
        },
        {
          label: t("Tax Exclusive"),
          value: "Exclusive",
        },
        {
          label: t("No Tax"),
          value: "NoTax",
        },
      ],
    }),
    {
      setKeyedAccounts: () => (keyedAccounts) => ({ keyedAccounts }),
    }
  ),
  onMount(
    ({
      item,
      setKeyedAccounts,
      xero,
      organisation,
      fetchCurrencies,
      fetchOrganisation,
      fetchAccounts,
      fetchTaxRates,
      fetchItems,
    }) =>
      () => {
        const isReceivable =
          item.Type === "ACCREC" ||
          item.Type === "ACCRECCREDIT" ||
          item.Type === "RECEIVE" ||
          item.QuoteNumber !== undefined;
        const isPayable =
          item.Type === "PURCHASEORDER" ||
          item.Type === "ACCPAY" ||
          item.Type === "ACCPAYCREDIT" ||
          item.Type === "SPEND";

        const inventory = fetchItems()
          .filter((i) =>
            isReceivable ? i.IsSold : isPayable ? i.IsPurchased : false
          )
          .map((i) =>
            set(i, "Key", compact([i.Code, i.Name]).join(": "))
          );
        return Promise.props({
          isReceivable,
          isPayable,
          currencies: fetchCurrencies(),
          inventory,
          indexedInventory: inventory.then((i) => keyBy(i, "Code")),
          organisation: fetchOrganisation(),
          accounts: fetchAccounts().then((accounts) => {
            const classes = [
              "REVENUE",
              "EQUITY",
              "LIABILITY",
              "EXPENSE",
              "ASSET",
            ];
            setKeyedAccounts(keyBy(accounts, "Code"));

            return chain(accounts)
              .filter((i) => {
                if (i.AllowPayable !== undefined && isPayable) {
                  return i.AllowPayable;
                }
                if (i.AllowReceivable !== undefined && isReceivable) {
                  return i.AllowReceivable;
                }
                return includes(classes, i.Class) && i.Code && i.Type !== 'BANK';
              })
              .map((i) => ({
                ...i,
                Description: `${i.Code} ${i.Name}`,
              }))
              .groupBy("Class")
              .map((options, label) => ({
                Description: capitalize(label),
                options,
              }))
              .value();
          }),
          taxRates: fetchTaxRates(),
        });
      }
  ),
  withPopover(),
  withPropsOnChange(
    (props, nextProps) => recalculate(props.item, nextProps),
    () => { }
  ),
  withPropsOnChange(["dispatch"], (props) => recalculate(null, props)),
  withHandlers({
    handleAddLines:
      ({ dispatch, item, isReceivable, isPayable, includeDiscount }) =>
        (lines) => {
          const customerDiscount = get(item, "Contact.Discount") || undefined;
          const customerTaxType =
            get(
              item,
              isReceivable
                ? "Contact.AccountsReceivableTaxType"
                : "Contact.AccountsPayableTaxType"
            ) || undefined;
          const customerAccountCode =
            get(
              item,
              isReceivable
                ? "Contact.SalesDefaultAccountCode"
                : isPayable
                  ? "Contact.PurchasesDefaultAccountCode"
                  : null
            ) || undefined;

          range(lines).map((i) =>
            dispatch(
              actions.push("local.LineItems", {
                _id: shortid(),
                DiscountRate: includeDiscount ? customerDiscount : undefined,
                TaxType: customerTaxType,
                AccountCode: customerAccountCode,
              })
            )
          );
        },
    removeLine:
      ({ dispatch }) =>
        (idx) => {
          dispatch(actions.remove("local.LineItems", idx));
        },
  })
);

export default withLineItems(LineItems);
